import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Chip,
} from '@mui/material';
import apiClient from '../../services/api';

const ShopsManagement = () => {
  const [rows, setRows] = useState([]);
  const [followerCounts, setFollowerCounts] = useState({});
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const response = await apiClient.get('/business-registrations');
        const data = response?.data?.data || [];
        if (!isMounted) return;
        setRows(data);

        // Public social-proof counts only — no follower PII.
        const ids = [
          ...new Set(
            data
              .map((r) => r.userId || r.user_id || r.businessId || r.business_id)
              .filter(Boolean)
              .map(String),
          ),
        ].slice(0, 100);
        if (ids.length) {
          try {
            const countsRes = await apiClient.get('/shop-followers/counts', {
              params: { ids: ids.join(',') },
            });
            if (isMounted) setFollowerCounts(countsRes?.data?.data || {});
          } catch {
            /* optional — table may be empty on first deploy */
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.response?.data?.message || 'Failed to load shops');
        }
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredRows = rows.filter((row) => {
    const text = `${row.businessName || ''} ${row.userPhone || ''} ${row.city || ''}`.toLowerCase();
    return text.includes(search.toLowerCase());
  });

  const countFor = (row) => {
    const id = String(row.userId || row.user_id || row.businessId || row.business_id || '');
    if (!id) return 0;
    return followerCounts[id] || 0;
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        Shops Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Followers = citizens who opted in for shop update alerts (count only; identities stay private).
      </Typography>
      <TextField
        fullWidth
        label="Search by shop name / phone / city"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mt: 2 }}
      />
      <Card sx={{ mt: 3 }}>
        <CardContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!error && filteredRows.length === 0 && (
            <Alert severity="info">No shop registrations found.</Alert>
          )}
          {filteredRows.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Shop</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Followers</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.businessName || '-'}</TableCell>
                    <TableCell>{row.userPhone || '-'}</TableCell>
                    <TableCell>{row.city || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={countFor(row) > 0 ? 'primary' : 'default'}
                        label={countFor(row)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status || 'unknown'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ShopsManagement;

